import { useCallback } from 'react'

import { format } from 'date-fns'
import { Ok } from 'ts-results'

import { useApi } from 'app/common/application'
import { getFormerDay } from 'app/common/presentation/getFormerDay'
import { InstalledConnection } from 'app/connections/types'

import {
  AudienceRevenueGetRequest,
  AudienceRevenueResponse,
  ConversionsGetRequest,
  ConversionsResponse,
  MatchingRateGetRequest,
  MatchingRateResponse,
  SessionsGetRequest,
  SessionsResponse,
  mapToConversions,
  mapToMatchingRate,
  mapToSortedAudienceDayRevenues,
  mapToSortedSessions,
} from '../types'

const getDatePeriodDates = (days: number) => {
  const now = new Date()
  const date = getFormerDay(days)

  return { fromDate: format(date, 'yyyy-MM-dd'), toDate: format(now, 'yyyy-MM-dd') }
}

const useAnalytics = () => {
  const { create } = useApi()

  const fetchConversions = useCallback(
    async (days: number) =>
      create<ConversionsGetRequest, ConversionsResponse>('/analytics/conversions/query', {
        content: getDatePeriodDates(days),
      }).then((result) => (result.ok ? Ok(mapToConversions(result.val.data)) : result)),
    [create],
  )

  const fetchMatchingRatio = useCallback(
    async (days: number, connections: InstalledConnection[]) =>
      create<MatchingRateGetRequest, MatchingRateResponse>('/profiles/matchingRate/query', {
        content: getDatePeriodDates(days),
      }).then((result) =>
        result.ok ? Ok(mapToMatchingRate(result.val.data, connections)) : result,
      ),
    [create],
  )

  const fetchRevenues = useCallback(
    async (days: number) =>
      create<AudienceRevenueGetRequest, AudienceRevenueResponse>('/analytics/signalRevenue/query', {
        content: getDatePeriodDates(days),
      }).then((result) =>
        result.ok ? Ok(mapToSortedAudienceDayRevenues(result.val.data)) : result,
      ),
    [create],
  )

  const fetchSessions = useCallback(
    async (days: number) =>
      create<SessionsGetRequest, SessionsResponse>('/analytics/sessions/query', {
        content: getDatePeriodDates(days),
      }).then((result) => (result.ok ? Ok(mapToSortedSessions(result.val.data)) : result)),
    [create],
  )

  return {
    fetchConversions,
    fetchMatchingRatio,
    fetchRevenues,
    fetchSessions,
  }
}

export default useAnalytics
